<template>
  <div id="login-component-element" class="login-box-inner login-component">
    <transition
      name="login-comp-trans"
      mode="out-in"
      @enter="resetHeight"
    >
      <!-- SCOPE SELECTOR - INTERNAL / EXTERNAL BOX-->
      <div
        key="loadingSelector"
        v-if="!loginMethodsLoaded"
        class="d-flex flex-column justify-content-center selector-option-wrapper loading-selector"
      >
        <div class="w-100 d-flex flex-column align-items-center justify-content-center">
          <b-spinner variant="secondary" label="Loading login options"></b-spinner>
          <div class="mt-3 text-center special-text text-muted font-size-100">Loading...</div>
        </div>
      </div>
      <div
        key="scopeSelector"
        v-else-if="shouldShowScopeSelector"
        class="d-flex flex-column justify-content-center selector-option-wrapper scope-selector"
      >
        <h1 class="login-scope-headline">Select Login Option:</h1>
        <div class="w-100">
          <b-button
            block
            variant="sharp-primary"
            size="lg"
            class="scope-selector-button"
            @click="() => selectScope('internal')"
          >
            <div class="scope-selector-button-text">
              <span 
                class="font-weight-bold font-size-120 d-block"
              >{{ internalLoginButtonContent.text }}</span>
              <span 
                v-if="internalLoginButtonContent.subtext" 
                class="mt-1 font-weight-normal d-block font-size-80 text-muted"
              >{{ internalLoginButtonContent.subtext }}</span>
            </div>
            <div class="scope-selector-button-arrow">
              <b-icon icon="chevron-right" font-scale="2"></b-icon>
            </div>
          </b-button>
        </div>
        <div class="mt-3 w-100">
          <b-button
            block
            variant="sharp-primary"
            size="lg"
            class="scope-selector-button"
            @click="() => selectScope('external')"
          >
          <div class="scope-selector-button-text">
            <span 
                class="font-weight-bold font-size-120 d-block"
              >{{ externalLoginButtonContent.text }}</span>
              <span 
                v-if="externalLoginButtonContent.subtext" 
                class="mt-1 font-weight-normal d-block font-size-80 text-muted"
              >{{ externalLoginButtonContent.subtext }}</span>
            </div>
            <div class="scope-selector-button-arrow">
              <b-icon icon="chevron-right" font-scale="2"></b-icon>
            </div>
          </b-button>
        </div>
      </div>
      <div
        v-else
        key="loginOptionsHolder"
        class="d-flex flex-column justify-content-center selector-option-wrapper login-options-holder"
      >
        <b-overlay 
          :show="showLoginPending || showSuccess" 
          no-wrap 
          opacity="0.9"
        >
          <template #overlay>
            <div 
              class="login-pending-success-outer p-4 d-flex flex-column align-items-center justify-content-center" 
              :class="showSuccess ? 'bg-transparent' : 'bg-transparent'"
              style="border-radius: 1rem; width: 150px; height: 150px;"
            >
              <transition name="logging-in-success-trans" mode="out-in" appear>
              <div
                v-if="showLoginPending && !showSuccess" 
                key="loggingIn" 
                class="w-100 d-flex flex-column align-items-center justify-content-center"
                style="border-radius: 1rem;"
              >
                <b-icon icon="shield-lock-fill" variant="dark" font-scale="3"></b-icon>
                <div class="mt-3 text-center special-text text-dark font-size-100">Checking<br />Credentials</div>
              </div>
              <div 
                v-else-if="showSuccess" 
                key="success" 
                class="w-100 d-flex flex-column align-items-center justify-content-center"
                style="border-radius: 1rem;"
              >
                <b-icon icon="shield-fill-check" variant="success" font-scale="3"></b-icon>
                <div class="mt-3 text-center special-text text-success-darker font-size-100">Success</div>
                <div class="mt-3 text-center">
                  <b-spinner variant="success" small></b-spinner>
                </div>
                <!-- <div class="success-dots-scope">
                  <span class="success-dots"></span>
                </div> -->
              </div>
              </transition>
            </div>
          </template>
        </b-overlay>
        <div class="">
          <!-- GENERAL / SHARED SSO LOGIN METHODS -->
          <div v-if="shouldShowGeneralLoginMethods" key="generalOptions">
            <h1 class="login-scope-headline">{{ headline }}</h1>
            <div class="mb-3 w-100">
              <PasswordLoginForm 
                @started="handlePasswordLoginStarted"
                @success="handlePasswordLoginSuccess"
                @error="handlePasswordLoginError"
                @finished="handlePasswordLoginFinished"
              />
            </div>
            <div v-if="generalLoginMethods.length > 0" class="sso-divider">
              <hr />
              <h3 class="sso-headline">{{ ssoHeadline }}</h3>
              <hr />
            </div>
            <AuthenticationMethodLine
              class="mb-2"
              v-for="loginMethod in generalLoginMethods"
              :key="loginMethod.id"
              :title="loginMethod.login_method_title"
              :logo="loginMethod.login_method_logo"
              :loginUrl="loginMethod.sso_login_url"
            ></AuthenticationMethodLine>
          </div>
          <!-- END OF GENERAL / SHARED SSO LOGIN METHODS -->
          <div v-else key="scopedOptions">
            <transition
              name="login-comp-trans"
              mode="out-in"
              @enter="resetHeight"
            >
            <div
              v-if="selectedScope == 'internal'"
              key="internalOption"
            >
              <h1 class="login-scope-headline">{{ headline }}</h1>
              <div
                v-if="isPasswordLoginEnabledForSelectedScope"
                class="mb-3 w-100"
              >
                <PasswordLoginForm 
                  @started="handlePasswordLoginStarted"
                  @success="handlePasswordLoginSuccess"
                  @error="handlePasswordLoginError"
                  @finished="handlePasswordLoginFinished"
                />
              </div>
              <div v-if="internalLoginMethods.length > 0" class="sso-divider">
                <hr />
                <h3 class="sso-headline">{{ ssoHeadline }}</h3>
                <hr />
              </div>
              <AuthenticationMethodLine
                class="mb-2"
                v-for="internalLoginMethod in internalLoginMethods"
                :key="internalLoginMethod.id"
                :title="internalLoginMethod.login_method_title"
                :logo="internalLoginMethod.login_method_logo"
                :loginUrl="internalLoginMethod.sso_login_url"
              ></AuthenticationMethodLine>
              <div class="mb-2 mt-4 d-flex align-items-center justify-content-between">
                <b-button
                  variant="minimal-primary"
                  size="md"
                  class="text-nowrap change-scope-button"
                  @click="backToScopeSelector"
                >&larr; Back</b-button>
                <b-button
                  variant="minimal-primary"
                  size="md"
                  class="text-nowrap change-scope-button"
                  @click="() => selectScope('external')"
                >
                  <span class="">Change to {{ externalLoginButtonContent.text }} &rarr;</span>
                </b-button>
              </div>
            </div>
            <div
              v-else-if="selectedScope == 'external'"
              key="externalOption"
            >
              <h1 class="login-scope-headline">{{ headline }}</h1>
              <div
                v-if="isPasswordLoginEnabledForSelectedScope"
                class="mb-3 w-100"
              >
                <PasswordLoginForm 
                  @started="handlePasswordLoginStarted"
                  @success="handlePasswordLoginSuccess"
                  @error="handlePasswordLoginError"
                  @finished="handlePasswordLoginFinished" 
                />
              </div>
              <div v-if="externalLoginMethods.length > 0" class="sso-divider">
                <hr />
                <h3 class="sso-headline">{{ ssoHeadline }}</h3>
                <hr />
              </div>
              <AuthenticationMethodLine
                class="mb-2"
                v-for="externalLoginMethod in externalLoginMethods"
                :key="externalLoginMethod.id"
                :title="externalLoginMethod.login_method_title"
                :logo="externalLoginMethod.login_method_logo"
                :loginUrl="externalLoginMethod.sso_login_url"
              ></AuthenticationMethodLine>
              <div class="mb-2 mt-4 d-flex align-items-center justify-content-between">
                <b-button
                  variant="minimal-primary"
                  size="md"
                  class="text-nowrap change-scope-button"
                  @click="backToScopeSelector"
                >&larr; Back</b-button>
                <b-button
                  variant="minimal-primary"
                  size="md"
                  class="text-nowrap change-scope-button"
                  @click="() => selectScope('internal')"
                >
                  <span class="">Change to {{ internalLoginButtonContent.text }} &rarr; </span>
                </b-button>
              </div>
            </div>
            </transition>
          </div>
        </div>
      </div>
      <!-- END OF LOGIN METHODS HOLDER -->
    </transition>
  </div>
</template>

<script>
  import { currentUserInfoMixin, tenantInfoMixin } from '../../js/custom-vue-mixins';
  import AuthenticationMethodLine from '@/components/auth/authentication-method/AuthenticationMethodLine.vue';
  import { suppliosApi } from '@/js/api-service';
  import PasswordLoginForm from './PasswordLoginForm.vue';

  export default {
    name: 'NewLogin',
    components: { AuthenticationMethodLine, PasswordLoginForm },
    props: {
      csrfToken: {
        type: String,
        required: true,
      },
    },
    mixins: [currentUserInfoMixin, tenantInfoMixin],
    data: function () {
      return {
        loginMethodsLoaded: false,
        showScopeSelector: true,
        selectedScope: 'general',
        loginMethods: [],
        showLoginPending: false,
        showSuccess: false,
        // ssoOption: 'external',
      };
    },
    computed: {
      headline() {
        // return the headline based on the selected scope
        // making it a computed so you can use other instance data in the future
        const headlinesByScope = {
          general: 'Sign In to your account',
          internal: 'Internal User Login',
          external: 'Supplier Login',
        };
        return headlinesByScope[this.selectedScope];
      },
      ssoHeadline() {
        // for the text above the SSO login methods
        // will depend on whether we're showing password and/or SSO login methods
        return this.isPasswordLoginEnabledForSelectedScope ? 'or use Single Sign-On' : 'Single Sign-On is Required';
      },
      internalLoginButtonContent() {
        // return object with { text, subtext } for now
        return {
          text: 'Internal User Login',
          subtext: this.isTenantSpecific ? `For internal users (employees) of ${this.tenantName}` : '',
        };
      },
      externalLoginButtonContent() {
        // return object with { text, subtext } for now
        return {
          text: 'Supplier Login',
          subtext: this.isTenantSpecific ? `For suppliers of ${this.tenantName}` : '',
        };
      },
      shouldShowGeneralLoginMethods() {
        return this.selectedScope === 'general' || !this.isTenantSpecific;
      },
      shouldShowScopeSelector() {
        return this.showScopeSelector && this.isTenantSpecific && this.loginMethods.length > 1;
      },
      isTenantSpecific() {
        return !(['', null].includes(this.$tenantInfo.tenant_id)); // check for '' or null in this.$tenantInfo.tenant_id -- should be a '' string if not tenant specific, but would be easy for us to change to None in backend (so null here)
      },
      tenantName() {
        return this.$tenantInfo.tenant_name || 'the host company';
      },
      // all these arrays of login methods will show NON-PASSWORD login methods
      generalLoginMethods() {
        return this.loginMethods.filter((auth) => auth.login_method_type !== 'password' && !auth.scope);
      },
      internalLoginMethods() {
        return this.loginMethods.filter((auth) => auth.login_method_type !== 'password' && (auth.scope == 'internal' || auth.scope == 'both'));
      },
      externalLoginMethods() {
        return this.loginMethods.filter((auth) => auth.login_method_type !== 'password' && (auth.scope == 'external' || auth.scope == 'both'));
      },
      // HELPERS
      isPasswordLoginEnabledForSelectedScope() {
        // cannot use this to get general login methods (when we have no tenant) - since auth.scope doesn't exist on those
        // return false;  // TEMP
        if (!this.isTenantSpecific) {
          // on non tenant specific pages, we always show password login
          return true;
        }
        return this.loginMethods.some((auth) => auth.login_method_type === 'password' && (auth.scope === this.selectedScope || auth.scope === 'both'));
      },
    },
    mounted: function () {
      this.getLoginMethods();
    },
    methods: {
      backToScopeSelector() {
        this.showScopeSelector = true;
        this.selectedScope = 'general';
      },
      selectScope(scope) {
        this.selectedScope = scope;
        this.showScopeSelector = false;
      },
      async getLoginMethods() {
        const loginMethods = await suppliosApi.auth.getLoginMethods();  // errors are handled in there for now
        this.loginMethods = loginMethods;
        this.loginMethodsLoaded = true;
      },
      getCurrentSelectorWrapperElementHeight() {
        const parentElement = document.getElementById('login-component-element');
        if (!parentElement) {
          return null;
        }
        // get the first child with the selector-option-wrapper class
        const wrapperElement = parentElement.querySelector('.selector-option-wrapper');
        return wrapperElement ? wrapperElement.offsetHeight : null;
      },
      setWrapperHeight() {
        // this is called from MULTIPLE transitions, we don't actually do anything with the entering element
        // instead, we just check the height of the wrapper element and set the parent element height to that
        // console.log('transition ended - enteringElement', enteringElement);
        // put the whole thing inside a nextTick?  need it many places, might as well do it all places... --> other method calls this wrapped in nextTick
        const wrapperHeight = this.getCurrentSelectorWrapperElementHeight();
        if (wrapperHeight) {
          // set the height of the parent element to the height of the wrapper element (so it transitions smoothly)
          const parentElement = document.getElementById('login-component-element');
          if (parentElement) {
            // console.log('setting parent element height to', wrapperHeight);
            parentElement.style.height = `${wrapperHeight}px`;
          }
        }
      },
      resetHeight() {
        this.$nextTick(() => {
          this.setWrapperHeight();
        });
      },
      // EVENT HANDLERS FROM PASSWORD LOGIN FORM
      // the PasswordLoginForm component may change height during it's transitions
      // so when we get events from it, we should recalculate the height of the parent element here
      handlePasswordLoginStarted() {
        console.log('password login started');
        // TODO - show loading state
        this.showLoginPending = true;
        this.resetHeight();
      },
      handlePasswordLoginSuccess(responseData) {
        console.log('password login success', responseData);
        // show success state, get the location from the response data (responseData.location) and redirect to that, possibly after some delay
        this.showSuccess = true;
        this.resetHeight();
        const redirectLocation = responseData.location;
        if (redirectLocation) {
          console.log('login successful - will redirect to:', redirectLocation);
          setTimeout(() => {
            window.location.href = redirectLocation;
          }, 750);  // 750 seems OK here 
        }
      },
      handlePasswordLoginError(error) {
        console.error('password login error', error);
        this.showLoginPending = false;
        this.resetHeight();
      },
      handlePasswordLoginFinished() {
        console.log('password login finished');
        this.showLoginPending = this.showSuccess ? false : this.showLoginPending;
        // only set that if showSuccess is false?
        this.resetHeight();
      },
    },
  };
</script>

<style lang="scss">
#login-component-element {

  transition: height 0.2s ease-out;

  // TRANSITION CLASSES
  .login-comp-trans-enter-active {
    transition: opacity 0.4s ease-out;  // come in slowly, slower than height change also
  }
  .login-comp-trans-leave-active {
    transition: opacity 0.2s ease-out;  // go out fast
  }
  .login-comp-trans-enter, .login-comp-trans-leave-to {
      opacity: 0;
  }

  .logging-in-success-trans-enter-active {
    transition: all 0.2s ease;  // come in slowly, slower than height change also
  }
  .logging-in-success-trans-leave-active {
    transition: all 0.1s ease;  // go out fast
  }
  .logging-in-success-trans-enter {
    opacity: 0;
    transform: translateY(30px);
    //transform: scale(0.9);
  }
  .logging-in-success-trans-leave-to {
    opacity: 0;
    transform: translateY(-30px);
  }

  .login-pending-success-outer {
    transition: all 0.4s ease-out;
  }


  // TEXT HEADINGS
  .login-scope-headline {
    font-size: 1.25rem;
    font-weight: 600;
    text-align: center;
    color: var(--gray-800);
    margin-bottom: 1.5rem;
  }
  .sso-divider {
    display: flex;
    column-gap: 0.75rem;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.75rem;
    margin-top: 0.75rem;
    hr {
      flex: 1;
      border: 0;
      border-top: 1px solid var(--gray-300);
    }
    .sso-headline {
      font-size: 0.8rem;
      font-weight: 500;
      text-align: center;
      white-space: nowrap !important;  // text-nowrap is same
      color: var(--gray-500);
      text-transform: uppercase;
      margin-bottom: 0;
    }
  }

  .cursor-pointer {
    cursor: pointer;
  }
  .selector-option-wrapper {
    // these are the things inside the transition - make sure they all have consistent min-height
    min-height: 250px;
  }
  .scope-selector-button {
    position: relative;
    padding-left: 2rem;
    padding-right: 2rem;
    padding-top: 1rem;
    padding-bottom: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    .scope-selector-button-text {
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
    }
    .scope-selector-button-arrow {
      position: absolute;
      right: 1.5rem;
      opacity: 0;
      transition: 0.2s all;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    &:hover {
      .scope-selector-button-arrow {
        right: 0.25rem;
        opacity: 1;
      }
    }
  }
  .change-scope-button {
    // tighten up x-padding so they fit better
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  // LOADING DOTS FOR SUCCESS 

  .success-dots-scope {
    $dot-size: 12px;
    $inner-dots: calc($dot-size + 2px);
    $outer-dots: calc($dot-size * 3 + 2px);

    .success-dots {
      //width: $dot-size;
      // width: 12px;  // adjust this to change size of dots - original was 12px
      // height: 12px;  // adjust this to change size of dots - original was 12px
      width: 10px;
      height: 10px;
      border-radius: 50%;
      display: block;
      margin: 15px auto;  // adjust this to change width of overall group of dots - original was 15px
      position: relative;
      //color: #FFF;
      color: var(--success);
      box-sizing: border-box;
      animation: successDotsAnimation 1s linear infinite;
    }

    @keyframes successDotsAnimation {
      0% {
        // box-shadow: 14px 0 0 -2px,  38px 0 0 -2px,  -14px 0 0 -2px,  -38px 0 0 -2px;
        box-shadow: 12px 0 0 -2px,  32px 0 0 -2px,  -12px 0 0 -2px,  -32px 0 0 -2px;
      }
      25% {
        // box-shadow: 14px 0 0 -2px,  38px 0 0 -2px,  -14px 0 0 -2px,  -38px 0 0 2px;
        box-shadow: 12px 0 0 -2px,  32px 0 0 -2px,  -12px 0 0 -2px,  -32px 0 0 2px;
      }
      50% {
        // box-shadow: 14px 0 0 -2px,  38px 0 0 -2px,  -14px 0 0 2px,  -38px 0 0 -2px;
        box-shadow: 12px 0 0 -2px,  32px 0 0 -2px,  -12px 0 0 2px,  -32px 0 0 -2px;
      }
      75% {
        // box-shadow: 14px 0 0 2px,  38px 0 0 -2px,  -14px 0 0 -2px,  -38px 0 0 -2px;
        box-shadow: 12px 0 0 2px,  32px 0 0 -2px,  -12px 0 0 -2px,  -32px 0 0 -2px;
      }
      100% {
        // box-shadow: 14px 0 0 -2px,  38px 0 0 2px,  -14px 0 0 -2px,  -38px 0 0 -2px;
        box-shadow: 12px 0 0 -2px,  32px 0 0 2px,  -12px 0 0 -2px,  -32px 0 0 -2px;
      }
    }
  }
}
</style>
